import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import ContactModal from '../components/ContactModal';
import MedicalInfoModal from '../components/MedicalInfoModal';

const { FiPlus, FiPhone, FiAlertTriangle, FiUser, FiTrash2, FiEdit, FiActivity, FiShare2, FiShield, FiUsers } = FiIcons;

export default function Emergency() {
  const { state, dispatch } = useFamilyContext();
  const { emergencyContacts, medicalInfo, familyMembers } = state;
  const [showContactModal, setShowContactModal] = useState(false);
  const [showMedicalModal, setShowMedicalModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editingMedical, setEditingMedical] = useState(null);

  const handleSaveContact = (contactData) => {
    if (editingContact) {
      dispatch({ type: 'UPDATE_EMERGENCY_CONTACT', payload: { ...editingContact, ...contactData } });
      setEditingContact(null);
    } else {
      dispatch({ type: 'ADD_EMERGENCY_CONTACT', payload: contactData });
    }
  };

  const handleSaveMedical = (medicalData) => {
    if (editingMedical) {
      dispatch({ type: 'UPDATE_MEDICAL_INFO', payload: { ...editingMedical, ...medicalData } });
      setEditingMedical(null);
    } else {
      dispatch({ type: 'ADD_MEDICAL_INFO', payload: medicalData });
    }
  };

  const handleDeleteContact = (id) => {
    if (confirm('Kontakt löschen?')) {
      dispatch({ type: 'DELETE_EMERGENCY_CONTACT', payload: id });
    }
  };

  const handleDeleteMedical = (id) => {
    if (confirm('Eintrag löschen?')) {
      dispatch({ type: 'DELETE_MEDICAL_INFO', payload: id });
    }
  };

  const copyShareLink = (member) => {
    if (!member.share_token) {
        alert('Kein Token vorhanden. Bitte Seite neu laden oder Admin kontaktieren.');
        return;
    }
    const link = `${window.location.origin}/#/shared/emergency/${member.share_token}`;
    navigator.clipboard.writeText(link);
    alert(`Notfall-Link für ${member.name} kopiert!`);
  };

  const getMedicalTypeLabel = (type) => {
      switch(type) {
          case 'medication': return 'Medikation';
          case 'allergy': return 'Allergie';
          case 'condition': return 'Erkrankung';
          default: return 'Sonstiges';
      }
  };

  const getMedicalTypeColor = (type) => {
      switch(type) {
          case 'medication': return 'text-blue-600 bg-blue-50 border-blue-100';
          case 'allergy': return 'text-red-600 bg-red-50 border-red-100';
          case 'condition': return 'text-purple-600 bg-purple-50 border-purple-100';
          default: return 'text-gray-600 bg-gray-50 border-gray-100';
      }
  };

  return (
    <div className="space-y-8 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Notfallzentrale</h1>
          <p className="text-gray-600 mt-2 dark:text-gray-400">Kontakte, medizinische Daten und Sicherheitsinfos</p>
        </div>
      </div>

      {/* SOS Banner */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start space-x-4 dark:bg-red-900/20 dark:border-red-800">
        <div className="bg-red-100 p-3 rounded-full dark:bg-red-800">
            <SafeIcon icon={FiAlertTriangle} className="w-8 h-8 text-red-600 dark:text-red-200" />
        </div>
        <div>
            <h2 className="text-lg font-bold text-red-800 dark:text-red-200">Im Notfall: 112 wählen</h2>
            <p className="text-red-700 mt-1 dark:text-red-300">
                Polizei: 110 • Ärztlicher Bereitschaftsdienst: 116 117 • Giftnotruf: 030 19240
            </p>
        </div>
      </div>

      {/* Family Share Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
          <h2 className="text-lg font-bold mb-4 flex items-center dark:text-white">
              <SafeIcon icon={FiShield} className="w-5 h-5 mr-2 text-green-600" />
              Notfall-Profile teilen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {familyMembers.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 dark:bg-gray-700 dark:border-gray-600">
                      <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-600">
                              {member.avatar || member.name.charAt(0)}
                          </div>
                          <span className="font-medium dark:text-white">{member.name}</span>
                      </div>
                      <button 
                        onClick={() => copyShareLink(member)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center px-2 py-1 bg-blue-50 rounded hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
                      >
                          <SafeIcon icon={FiShare2} className="w-4 h-4 mr-1" />
                          Link
                      </button>
                  </div>
              ))}
          </div>
      </div>

      {/* Emergency Contacts */}
      <div>
        <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold dark:text-white">Wichtige Kontakte</h2>
            <button
                onClick={() => { setEditingContact(null); setShowContactModal(true); }}
                className="text-sm bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 flex items-center dark:bg-gray-700 dark:text-gray-300"
            >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" /> Hinzufügen
            </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {emergencyContacts.map((contact, index) => (
            <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:border-red-200 transition-colors dark:bg-gray-800 dark:border-gray-700"
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center dark:bg-gray-700">
                            <SafeIcon icon={FiUser} className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 dark:text-white">{contact.name}</h3>
                            <p className="text-xs text-gray-500 uppercase font-medium tracking-wide dark:text-gray-400">{contact.role}</p>
                        </div>
                    </div>
                    <div className="flex space-x-1">
                        <button onClick={() => { setEditingContact(contact); setShowContactModal(true); }} className="text-gray-400 hover:text-blue-600 p-1">
                            <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteContact(contact.id)} className="text-gray-400 hover:text-red-600 p-1">
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                
                <a href={`tel:${contact.phone}`} className="flex items-center justify-center w-full py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-medium mb-3 dark:bg-green-900/20 dark:text-green-400">
                    <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2" />
                    {contact.phone}
                </a>

                {contact.people && contact.people.length > 0 && (
                   <div className="flex flex-wrap gap-1 mb-3">
                       {contact.people.map(pid => {
                           const m = familyMembers.find(fm => fm.id === pid);
                           return m ? (
                               <span key={pid} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full dark:bg-gray-700 dark:text-gray-300">
                                   {m.name}
                               </span>
                           ) : null;
                       })}
                   </div>
                )}
                
                {contact.address && (
                    <p className="text-sm text-gray-600 mt-2 px-1 dark:text-gray-400">
                        📍 {contact.address}
                    </p>
                )}
            </motion.div>
            ))}
        </div>
      </div>

      {/* Medical Info Section */}
      <div>
        <div className="flex justify-between items-center mb-4 mt-8">
            <h2 className="text-xl font-bold dark:text-white flex items-center">
                <SafeIcon icon={FiActivity} className="w-6 h-6 mr-2 text-red-500" />
                Medizinische Informationen
            </h2>
            <button
                onClick={() => { setEditingMedical(null); setShowMedicalModal(true); }}
                className="text-sm bg-red-50 text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-100 flex items-center dark:bg-red-900/30 dark:text-red-300"
            >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" /> Eintrag
            </button>
        </div>

        {medicalInfo.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-300 dark:bg-gray-800 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400">Keine medizinischen Daten hinterlegt.</p>
            </div>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {medicalInfo.map((info) => (
                    <div key={info.id} className={`p-4 rounded-xl border flex flex-col ${getMedicalTypeColor(info.type)} dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200`}>
                        <div className="flex justify-between items-start mb-2">
                             <span className="text-xs font-bold uppercase tracking-wider opacity-75 border border-current px-1.5 py-0.5 rounded">
                                 {getMedicalTypeLabel(info.type)}
                             </span>
                             <div className="flex space-x-1">
                                <button onClick={() => { setEditingMedical(info); setShowMedicalModal(true); }} className="opacity-50 hover:opacity-100 p-1"><SafeIcon icon={FiEdit} className="w-4 h-4" /></button>
                                <button onClick={() => handleDeleteMedical(info.id)} className="opacity-50 hover:opacity-100 p-1"><SafeIcon icon={FiTrash2} className="w-4 h-4" /></button>
                             </div>
                        </div>
                        <h3 className="text-lg font-bold mb-1">{info.title}</h3>
                        {(info.dosage || info.frequency) && (
                            <div className="text-sm opacity-90 mb-2">
                                {info.dosage && <span className="mr-2 font-medium">{info.dosage}</span>}
                                {info.frequency && <span>({info.frequency})</span>}
                            </div>
                        )}
                        {info.description && <p className="text-sm opacity-80 italic mb-3">{info.description}</p>}
                        
                        <div className="mt-auto pt-3 border-t border-current border-opacity-20 flex flex-wrap gap-1">
                            {info.people && info.people.map(personId => {
                                const member = familyMembers.find(m => m.id === personId);
                                return member ? (
                                    <span key={personId} className="text-xs bg-white bg-opacity-50 px-2 py-0.5 rounded-full font-medium dark:bg-gray-700">
                                        {member.name}
                                    </span>
                                ) : null;
                            })}
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>
      
      {showContactModal && (
        <ContactModal
          contact={editingContact}
          onClose={() => setShowContactModal(false)}
          onSave={handleSaveContact}
        />
      )}

      {showMedicalModal && (
        <MedicalInfoModal
            info={editingMedical}
            onClose={() => setShowMedicalModal(false)}
            onSave={handleSaveMedical}
        />
      )}
    </div>
  );
}